import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as userApi from '../api/User';
import { IUserCompanies, IUserProfile, IUserSettings } from '../util/DataModelInterface'
import { DataTable } from '../components/DataTable';
import { IFilterButtonData } from '../components/TableFilters';
import { Spinner } from '../components/Spinner';
import { useNavigate } from 'react-router-dom';
import parse from 'html-react-parser';
import { Typeahead } from 'react-bootstrap-typeahead';
import { ConfirmModal } from '../components/ConfirmModal';
import useToastHandler from '../hooks/useToastHandler';

interface IUserTableData {
  // UI columns: name, companies, reporting, info, state
  name: string;
  companies: string | JSX.Element | JSX.Element[];
  reporting?: JSX.Element;
  info?: JSX.Element;
  state: string;
  actions: JSX.Element;
  reporter: string;
  information: string;
}

const BUTTONS = [
  'activeusers',
  'reportingusers',
  'infousers',
  'archivedusers'
]

export const Users = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { addToast } = useToastHandler();
    const [users, setUsers] = useState([] as IUserProfile[]);
    const [loading, setLoading] = useState(false);
    const [confirmArchModalActive, setConfirmArchModalActive] = useState(false);
    const [confirmModalTitle, setConfirmModalTitle] = useState("");
    const [confirmModalContent, setConfirmModalContent] = useState("");
    const [selectedUser, setSelectedUser] = useState({} as IUserProfile);
    const [userTableData, setUserTableData] = useState([] as IUserTableData[]);
    const [filters, setFilters] = useState([] as IFilterButtonData[]);
    const [selectedFilterButton, setSelectedFilterButton] = useState(0);
    const [activeUsers, setActiveUsers] = useState([] as IUserProfile[]);
    const [reportingUsers, setReportingUsers] = useState([] as IUserProfile[]);
    const [infoUsers, setInfoUsers] = useState([] as IUserProfile[]);
    const [archivedUsers, setArchivedUsers] = useState([] as IUserProfile[]);
    const [selections, setSelections] = useState([] as IUserProfile[]);

    useEffect(() => {
      setLoading(true);
      update()
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = async (force: boolean = false) => {
      const comparer = (a: any, b: any) => {
        return a.name.localeCompare(b.name);
      }
      await userApi.getAllUsers(force).then((data: any) => {
          if (data?.error) {
            addToast(data.error, 'users', 'fetch');
          } else if (data) {
            data.sort(comparer);
            setUsers(data);
          }
      });
    }

    useEffect(() => {
      let selected = selections.length > 0 ? users.filter(u => u.user_name === selections[0].user_name) : [];
      let userSet = selections.length > 0 ? selected : users;
      parseTableData(userSet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selections, users]);

    const confirmArchiveUser = (user: IUserProfile) => {
      let changedUser = {...user} as IUserProfile;
      changedUser.archived = !user.archived;
      setSelectedUser(changedUser);

      let title = changedUser.archived ? intl.formatMessage({id: 'confirm.archive'}) : intl.formatMessage({id: 'confirm.activate'});
      let textContent = changedUser.archived ? intl.formatMessage({id: 'confirm.archive.content'}) : intl.formatMessage({id: 'confirm.activate.content'});

      setConfirmModalTitle(title);
      setConfirmModalContent(textContent);
      setConfirmArchModalActive(true);
    }

    const userActionButtons = (user: IUserProfile) => {
      let archived = user.archived;
      let icon = archived ? 'fa-check' : 'fa-archive';
      let text = archived ? 'users.activateuser' : 'users.archiveuser';
      return (
        <>
          <button className="btn btn-secondary" title={intl.formatMessage({id: 'users.modifyuser'})}
            onClick={ () => { navigate('/usermanagement/user', { replace: false, state: { user }});}}>
            <i className="fa fa-edit me-2"></i>
            <span><FormattedMessage id={'users.modifyuser'}/></span>
          </button>
          <button className="btn btn-secondary" title={intl.formatMessage({id: text})}
            onClick={ () => confirmArchiveUser(user) }>
            <i className={"fa me-2 " + icon}></i>
            <span><FormattedMessage id={text}/></span>
          </button>
        </>
      );
    }

    const checkedIcon = () => <i className="fa fa-check" title="checkIcon"></i>;

    const compList = (comps: IUserCompanies[], separator: string = '<br/>') => {
      let compList= "";
      comps.forEach((c: IUserCompanies, id: number) => {
        compList = compList + c.name;
        if (id < (comps.length - 1)) {
          compList = compList + separator;
        }
      });
      return parse(compList);
    }

    const foundItem = (sel: IUserProfile[]) => {
      let selItem = sel[0];
      console.log("found", selItem);
      setSelections(sel);
    }

    const filterByFields = ['user_name', 'name'];

    const parseTableData = (users: IUserProfile[]) => {
      let data = [] as IUserTableData[];
      let activeUsers = [] as IUserProfile[];
      let archivedUsers = [] as IUserProfile[];
      let reportingUsers = [] as IUserProfile[];
      let infoUsers = [] as IUserProfile[];
      users.forEach((u, i) => {
        data[i] = {
          name: u.name,
          companies: compList(u.companies),
          reporting: u.reporter ? checkedIcon() : undefined,
          info: u.information ? checkedIcon() : undefined,
          state: u.archived ? intl.formatMessage({id: 'users.archived'}) : intl.formatMessage({id: 'users.active'}),
          actions: userActionButtons(u),
          reporter: u.reporter ? 'reporter' : '',
          information: u.information ? 'information' : '',
        };
        if (!u.archived) activeUsers.push(u);
        if (u.archived) archivedUsers.push(u);
        if (u.reporter) reportingUsers.push(u);
        if (u.information) infoUsers.push(u);
      });
      setActiveUsers(activeUsers);
      setReportingUsers(reportingUsers);
      setInfoUsers(infoUsers);
      setArchivedUsers(archivedUsers);
      setFilters([
        {
          button: BUTTONS[0], count: activeUsers.length,
          filters: [{column: 'state', filterValues: [intl.formatMessage({id: 'users.active'})]},
                    {column: 'reporter', filterValues: []},
                    {column: 'information', filterValues: []}]
        },
        {
          button: BUTTONS[1], count: reportingUsers.length,
          filters: [{column: 'state', filterValues: []},
                    {column: 'reporter', filterValues: ['reporter']},
                    {column: 'information', filterValues: []}]
        },
        {
          button: BUTTONS[2], count: infoUsers.length,
          filters: [{column: 'state', filterValues: []},
                    {column: 'reporter', filterValues: []},
                    {column: 'information', filterValues: ['information']}]
        },
        {
          button: BUTTONS[3], count: archivedUsers.length,
          filters: [{column: 'state', filterValues: [intl.formatMessage({id: 'users.archived'})]},
                    {column: 'reporter', filterValues: []},
                    {column: 'information', filterValues: []}]
        }
      ]);
      setUserTableData(data);
    }

    const exportUsers = () => {
      let jsonData = [] as any;

      console.log("EXPORT! selectedFilterButton:", selectedFilterButton);

      let selectedUsers = [] as IUserProfile[];
      switch (selectedFilterButton){
        case 0:
          selectedUsers = activeUsers;
          break;
        case 1:
          selectedUsers = reportingUsers;
          break;
        case 2:
          selectedUsers = infoUsers;
          break;
        case 3:
          selectedUsers = archivedUsers;
          break;
      }

      selectedUsers.forEach((user: IUserProfile) => {
        let row = {} as any;
        row[intl.formatMessage({id: 'users.name'})] = user.name;
        row[intl.formatMessage({id: 'users.email'})] = user.user_name;
        row[intl.formatMessage({id: 'users.companies'})] = compList(user.companies, ', ');
        row[intl.formatMessage({id: 'users.phone'})] = user.phone;
        jsonData.push(row);
      });

      userApi.exportUsers(
      { header: intl.formatMessage({id: 'users.' + BUTTONS[selectedFilterButton]}),
        data: jsonData
      }).then((data: any) => {
        if (data?.error) {
          addToast(data.error, 'users', 'fetch');
        }
      })
  }

    const toolBar = () => {
      return(
      <div className="toolbar">
        <span className="item-actions ms-auto">
          <button key='toolbarbutton' className="btn btn btn-primary btn-reminder"
            disabled={users.length === 0}
            onClick={() => exportUsers() }>
            <i className="me-2 fa fa-download" aria-hidden="true"></i>
            <FormattedMessage id={'button.report.download'}/>
          </button>
        </span>
      </div>);
    }

    const archiveUser = async () => {

      let user =  {
        user_id: selectedUser.user_id,
        user_name: selectedUser.user_name,
        name: selectedUser.name,
        archived: selectedUser.archived
      } as IUserSettings;

      setLoading(true);
      let resp = await userApi.archiveUser(user);
      if (resp?.error) {
        addToast(resp.error, 'users', 'archive');
      } else {
        await update(true);
      }
      setLoading(false);
    }

    const confirmContent = () => <div>{confirmModalContent}</div>;

    return (
      <>
        <Spinner active={loading} />
        <ConfirmModal
          active={confirmArchModalActive}
          modalId='arhciveUserModal'
          onClose={() => setConfirmArchModalActive(false)}
          title={confirmModalTitle}
          content={confirmContent()}
          confirmbutton={
            {
              name: confirmModalTitle,
              class: 'btn btn-primary',
              onClick: () => { archiveUser(); setConfirmArchModalActive(false) }
            }
          }
          />
        <h1><FormattedMessage id="users.title"/></h1>
        <p className="info"><i className="fa fa-info-circle me-2"></i><FormattedMessage id="users.title.info"/></p>

        <div className="controls d-flex justify-content-end my-5 align-items-center">

          <div className="search-group me-md-auto">
            {/* <label className="form-control-label sr-only" htmlFor="search">Search:</label> */}
            <Typeahead
              id="typeahead-search-comp"
              inputProps={{
                'aria-label': intl.formatMessage({id: 'users.search.text'}),
              }}
              clearButton
              filterBy={filterByFields}
              labelKey="name"
              onChange={(sel) => {foundItem(sel as IUserProfile[]);}}
              options={users}
              selected={selections}
              placeholder={intl.formatMessage({id: 'users.search.text'})}
              renderMenuItemChildren={(option) => (
                <div>
                  {(option as IUserProfile).name}
                  <div>
                    <small>{(option as IUserProfile).user_name}</small>
                  </div>
                </div>
              )}
            />
          </div>
          <button className="btn btn-primary"
            onClick={ () => { navigate('/usermanagement/user', { replace: false, state: {}});}}
          >
            <i className="fa fa-plus-circle mr-2"></i>
            <FormattedMessage id="users.adduser"/>
          </button>
        </div>

        {userTableData.length > 0 &&
          <DataTable
            tableheadings={['name', 'companies', 'reporting', 'info', 'state', 'actions']}
            filters={filters}
            tabledata={userTableData} tableType='users' tableId='usersList'
            toolbar={toolBar()}
            showFilters={selections.length > 0 ? false : true}
            hiddenColumns={['reporter','information']}
            setSelectedButton={(id) => setSelectedFilterButton(id)}
            filterSet={BUTTONS[selectedFilterButton]}
            optSortableColumns={['name']}
          />
        }
      </>
    );
};